import { useState, FormEvent } from 'react';

const INQUIRY_TYPES = [
  'General Inquiry',
  'Trading Partnership',
  'Supplier Inquiry',
  'Investment Relations',
  'Media & Press',
  'Careers',
];


export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [selectedInquiry, setSelectedInquiry] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();

    const name = (form.elements.namedItem('name') as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
    const company = (form.elements.namedItem('company') as HTMLInputElement).value.trim();
    const phone = (form.elements.namedItem('phone') as HTMLInputElement).value.trim();
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value.trim();

    if (!name || !email || !message) return;

    data.append('name', name);
    data.append('email', email);
    data.append('company', company);
    data.append('phone', phone);
    data.append('inquiry_type', selectedInquiry);
    data.append('message', message);

    setStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d76jb1l4hjod66j2es40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setSelectedInquiry('');
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-[#071525] relative overflow-hidden">

      {/* Top Banner */}
      <div className="relative w-full flex items-center justify-center pt-24 pb-10">
        <div className="text-center px-6">
          <p className="text-[#d4571a] text-xs font-bold tracking-widest uppercase mb-3">
            Get In Touch
          </p>
          <h2 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
            Contact Us
          </h2>
          <p className="text-white/60 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            Whether you&apos;re a potential partner, supplier, or investor — we welcome
            your enquiry.
          </p>
        </div>
      </div>

      {/* ── Grid Texture ── */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg,transparent,transparent 60px,rgba(212,87,26,0.6) 60px,rgba(212,87,26,0.6) 61px),repeating-linear-gradient(90deg,transparent,transparent 60px,rgba(212,87,26,0.6) 60px,rgba(212,87,26,0.6) 61px)',
          }}
        />
      </div>

      {/* ── Main Content ── */}
      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 pb-24 pt-10">

          {/* ─── Form (full width) ─── */}
          <div>
            <div className="bg-white/[0.03] border border-white/8 rounded-2xl p-8">
              <h3 className="text-white text-xl font-bold mb-1">Send us a Message</h3>
              <p className="text-white/40 text-sm mb-7">Fill in the form below and our team will get back to you promptly.</p>

              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">

                {/* Inquiry Type */}
                <div className="flex flex-col gap-2">
                  <label className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                    Inquiry Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {INQUIRY_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setSelectedInquiry(type === selectedInquiry ? '' : type)}
                        className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-full border transition-all duration-200 cursor-pointer ${
                          selectedInquiry === type
                            ? 'border-[#d4571a] bg-[#d4571a]/20 text-white'
                            : 'border-white/10 bg-white/[0.03] text-white/50 hover:border-white/30 hover:text-white/80'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                      Full Name <span className="text-[#d4571a]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className="bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4571a]/50 transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                      Email <span className="text-[#d4571a]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      className="bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4571a]/50 transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Company + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="Company Ltd"
                      className="bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4571a]/50 transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 555 000 0000"
                      className="bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4571a]/50 transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                      Message <span className="text-[#d4571a]">*</span>
                    </label>
                    <span className="text-white/30 text-xs">{charCount}/500</span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="Tell us about your enquiry — the more detail, the better we can assist you..."
                    className="bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4571a]/50 transition-colors duration-200 resize-none"
                  />
                </div>

                {/* Status messages */}
                {status === 'success' && (
                  <div className="flex items-center gap-3 text-emerald-400 text-sm bg-emerald-400/10 border border-emerald-400/20 rounded-xl px-4 py-3">
                    <i className="ri-checkbox-circle-line text-lg shrink-0" />
                    <span>Message sent — we&apos;ll be in touch within one business day.</span>
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-3 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                    <i className="ri-error-warning-line text-lg shrink-0" />
                    <span>Something went wrong. Please try again or email us directly.</span>
                  </div>
                )}

                {/* Submit */}
                <div className="flex items-center justify-between flex-wrap gap-4 pt-1">
                  <p className="text-white/25 text-xs leading-relaxed max-w-xs">
                    By submitting this form you agree to our privacy policy and terms of use.
                  </p>
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="whitespace-nowrap bg-[#d4571a] hover:bg-[#bf4d17] active:bg-[#a84415] disabled:opacity-60 transition-colors duration-200 text-white text-sm font-semibold px-8 py-3.5 rounded-lg cursor-pointer flex items-center gap-2"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-line text-base" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
      </div>
    </section>
  );
}
